/**
 * Palmistry 101 — copy + structured data for the interactive course page.
 * Hotspot positions are percentages of the hand figure wrapper (cropped hand view).
 */

export const PALMISTRY_HAND_SVG =
  process.env.REACT_APP_PALMISTRY101_HAND_SVG_URL ||
  'https://d1yei2z3i6k35z.cloudfront.net/15470577/6991be9bbc80a_Palmistry-with-Astrological-Analogies.svg';

/** Fallback if CDN fails (same file in public/) */
export const PALMISTRY_HAND_SVG_FALLBACK = '/palmistry-101-hand.svg';

export const PALMISTRY_PRICE_LABEL = '$9';

export const PALMISTRY_CHECKOUT_URL = process.env.REACT_APP_PALMISTRY101_CHECKOUT_URL || '';

export const PALMISTRY_PDF_URL = process.env.REACT_APP_PALMISTRY101_PDF_URL || '';

export const studyAreas = [
  {
    id: 'heart-line',
    group: 'lines',
    label: 'Heart line',
    title: 'Heart line',
    short: 'Emotional style, connection, and responsiveness',
    explanation:
      'The heart line is often read as one lens on how you meet closeness: how you express affection, what soothes you, and how you pace intimacy. In this course, it is not a verdict on love or worth — it is a pattern you observe in context.',
    variations:
      'A clear, steady line may suggest emotional directness; a softer or more changeable line may suggest nuance, sensitivity, or shifting needs over time. Depth and continuity can hint at how strongly the theme shows up — not how “good” a person you are.',
    caution:
      'Avoid turning a single feature into a final story about relationships. Pair what you see with the rest of the hand and the person’s lived reality.',
    observe:
      'Notice whether this line feels clear, faint, curved, straight, broken, forked, or chained — and whether it reads calm or busy alongside the rest of the palm.'
  },
  {
    id: 'head-line',
    group: 'lines',
    label: 'Head line',
    title: 'Head line',
    short: 'Thinking style, focus, and how you process',
    explanation:
      'The head line offers a window into mental tempo: focus versus diffusion, practicality versus imagination, and how you like to solve problems. One possible expression is a long, steady line that suggests sustained concentration; another is a shorter or more fragmented line that may suggest quick pivots or many interests.',
    variations:
      'Curvature can hint at flexibility; a straighter line may suggest a more linear style. Interruptions may map to chapters of learning — not “damage.”',
    caution:
      'Do not treat breaks as automatic proof of trauma or failure. Read them as potential shifts in mindset or environment.',
    observe:
      'Trace length, depth, and whether the line lifts toward the upper palm or stays practical and close to the life line.'
  },
  {
    id: 'life-line',
    group: 'lines',
    label: 'Life line',
    title: 'Life line',
    short: 'Vitality, grounding, and how you move through change',
    explanation:
      'Despite old myths, the life line is not a measure of lifespan. It is better read as a map of energy, resilience, and how you re-ground after stress. A strong arc may suggest a robust relationship to body and rhythm; a quieter line may suggest sensitivity or a more inward tempo.',
    variations:
      'Forks, islands, or changes in depth can suggest transitions — new chapters rather than fixed fate.',
    caution:
      'Never use the life line to imply illness or doom. Keep language observational and compassionate.',
    observe:
      'Look at the arc’s depth, where it begins, and how it relates to the thumb — not how “long” it appears.'
  },
  {
    id: 'fate-line',
    group: 'lines',
    label: 'Fate / Saturn line',
    title: 'Fate line (when present)',
    short: 'Sense of path, responsibility, and outer structure',
    explanation:
      'Not every hand shows a strong fate line — and that is normal. When it appears, it may suggest how a person experiences vocation, duty, or a felt sense of trajectory. It can describe how outer structures (work, roles, timing) meet inner choice.',
    variations:
      'A clear central rise may suggest a strong through-line of purpose; a fragmented or wandering line may suggest exploration, reinvention, or multiple careers.',
    caution:
      'Avoid “destiny” absolutes. This line is one thread in a larger weave.',
    observe:
      'Notice whether the line rises toward a finger, starts late, or crosses the palm — and ask what life chapter that might echo.'
  },
  {
    id: 'mount-jupiter',
    group: 'mounts',
    label: 'Mount of Jupiter',
    title: 'Mount of Jupiter (index base)',
    short: 'Agency, leadership style, and healthy pride',
    explanation:
      'This mount can highlight how ambition shows up: quietly or visibly, through service or visibility. Prominence may suggest a stronger emphasis on initiative and standards.',
    variations:
      'Fullness here may suggest drive and visibility needs; a softer mount may suggest leadership through collaboration.',
    caution:
      'Do not confuse prominence with ego “good” or “bad.” Context matters.',
    observe:
      'Compare both palms — which mount stands out relative to the rest?'
  },
  {
    id: 'mount-saturn',
    group: 'mounts',
    label: 'Mount of Saturn',
    title: 'Mount of Saturn (middle base)',
    short: 'Depth, boundaries, and seriousness of purpose',
    explanation:
      'Saturn here can speak to patience, sobriety, and the willingness to stay with hard questions. It may suggest a taste for craft, research, or careful pacing.',
    variations:
      'A pronounced mount may suggest strong inner standards; a quieter mount may suggest flexibility or lighter framing.',
    caution:
      'Avoid cold or gloomy stereotypes — read discipline as a neutral strength.',
    observe:
      'Feel for firmness versus softness at the base of the middle finger.'
  },
  {
    id: 'mount-apollo',
    group: 'mounts',
    label: 'Mount of Apollo / Sun',
    title: 'Mount of Apollo (ring base)',
    short: 'Creative joy, visibility, and warmth',
    explanation:
      'Apollo’s territory often links to expression: art, performance, warmth, and the desire to be seen in a truthful way. It can also map to optimism and social radiance.',
    variations:
      'Strong fullness may suggest a need for creative outlet or recognition; moderation may suggest private satisfaction.',
    caution:
      'Do not equate fullness with vanity — it may simply be creative fuel.',
    observe:
      'Compare texture and prominence with neighboring mounts.'
  },
  {
    id: 'mount-mercury',
    group: 'mounts',
    label: 'Mount of Mercury',
    title: 'Mount of Mercury (pinky base)',
    short: 'Communication, wit, and practical cleverness',
    explanation:
      'Mercury here may suggest skill with language, commerce, healing hands, or quick pattern recognition. It can also hint at nervous system aliveness.',
    variations:
      'A lively mount may suggest talkative, agile energy; a quieter mount may suggest slower, more deliberate speech.',
    caution:
      'Avoid labeling someone “dishonest” from this mount alone.',
    observe:
      'Watch how this mount pairs with the head line and finger length.'
  },
  {
    id: 'mount-venus',
    group: 'mounts',
    label: 'Mount of Venus',
    title: 'Mount of Venus (thumb base)',
    short: 'Sensuality, loyalty, and capacity for comfort',
    explanation:
      'Venus speaks to warmth, loyalty, aesthetic pleasure, and how you restore yourself. Fullness can suggest strong appetite for beauty, touch, or steadiness in bonds.',
    variations:
      'Soft fullness may suggest gentleness; very firm fullness may suggest strong appetites and stamina.',
    caution:
      'Keep readings body-neutral and consent-aware.',
    observe:
      'Compare the ball of the thumb on both hands for symmetry or emphasis.'
  },
  {
    id: 'mount-moon',
    group: 'mounts',
    label: 'Mount of Moon',
    title: 'Mount of Moon (outer edge)',
    short: 'Imagination, intuition, and inner tides',
    explanation:
      'The lunar mount can reflect dream life, empathy, and how you process the unseen. It may suggest sensitivity to atmosphere and timing.',
    variations:
      'A pronounced mount may suggest rich inner life; a quieter mount may suggest grounding in the concrete.',
    caution:
      'Avoid mystical exaggeration — stay observational.',
    observe:
      'Notice whether lines cross this area or whether the mount stands out against the palm’s edge.'
  },
  {
    id: 'hand-shape',
    group: 'structure',
    label: 'Hand shape',
    title: 'Overall hand shape',
    short: 'Elemental tendencies — held lightly',
    explanation:
      'Many readers notice whether a hand feels long and rectangular (air), square and sturdy (earth), oblong and flexible (water), or angular and lean (fire). These are metaphors for temperament — not rigid boxes. One possible expression is a square palm with practical patience; another is a longer palm with exploratory curiosity.',
    variations:
      'Mixed features are common. Emphasize dominant qualities without erasing nuance.',
    caution:
      'Never use hand shape to justify stereotypes about gender, culture, or worth.',
    observe:
      'Step back and ask: does this hand feel compact or elongated, flexible or firm — before you zoom into lines?'
  }
];

/** Hotspot boxes: percentages of the figure box (hand-focused crop) */
export const studyHotspots = {
  'heart-line': { left: 26, top: 24, width: 50, height: 14 },
  'head-line': { left: 28, top: 38, width: 46, height: 12 },
  'life-line': { left: 6, top: 34, width: 26, height: 44 },
  'fate-line': { left: 40, top: 36, width: 12, height: 42 },
  'mount-jupiter': { left: 34, top: 6, width: 16, height: 20 },
  'mount-saturn': { left: 46, top: 4, width: 16, height: 20 },
  'mount-apollo': { left: 58, top: 8, width: 15, height: 20 },
  'mount-mercury': { left: 70, top: 18, width: 14, height: 22 },
  'mount-venus': { left: 4, top: 44, width: 22, height: 28 },
  'mount-moon': { left: 78, top: 32, width: 16, height: 26 },
  'hand-shape': { left: 12, top: 12, width: 76, height: 76 }
};

export const defaultStudyId = 'heart-line';

/** Interactive hand: preview visitors may explore only these study ids (full course unlocks all). */
export const PALMISTRY_PREVIEW_STUDY_IDS = ['heart-line', 'head-line'];

/** Single primary label for all purchase CTAs (hero, sticky, purchase block). */
export const PALMISTRY_PRIMARY_CTA_LABEL = 'Get Instant Access';

export const learningOutcomes = [
  'How to identify your hand type and what it may suggest about your nature',
  'How to read the major lines with nuance — without fear-based myths',
  'How the mounts show desire, drive, and hidden strengths',
  'How to spot turning points and “chapters” without turning the reading into fate',
  'How to create readings that feel grounded, accurate, and empowering'
];

export const lessons = [
  {
    id: 'l1',
    title: 'Lesson 1 — How to Begin',
    summary: 'Orientation, ethics of observation, and a calm frame for reading.',
    body: [
      'Palmistry, in this course, is approached as symbolic pattern work — similar to learning a language of shapes. Rather than treating the hand as a sentence already written forever, you learn to notice emphasis, rhythm, and context.',
      'Dominant features are the ones your eye returns to; minor features add shading. The goal is not to catalogue everything at once, but to build a fair sketch and refine it with patience.'
    ],
    prompts: [
      'Before interpreting, spend two minutes simply describing what you see — texture, proportion, and line quality — without “meaning.”'
    ],
    more: [
      'Compare both hands when you can: one is often read as innate tendency, the other as developed expression — but hold this lightly and let the person’s story lead.'
    ]
  },
  {
    id: 'l2',
    title: 'Lesson 2 — Hand Type',
    summary: 'Elemental language as metaphor — flexible, humane, non-boxy.',
    body: [
      'Hand shape language (earth, air, fire, water) is a teaching scaffold. It helps beginners find vocabulary for temperament — whether someone feels steady, mental, quick, or fluid.',
      'The point is not to trap anyone in a label. Mixed shapes are the norm. If a hand borrows from more than one style, name the blend and stay curious.'
    ],
    prompts: [
      'Which single word — steady, curious, swift, or fluid — fits the hand best today? Why?'
    ],
    more: [
      'Finger length relative to palm shifts the story: longer fingers may suggest refinement and detail; shorter fingers may suggest directness and momentum — always as possibility, not proof.'
    ]
  },
  {
    id: 'l3',
    title: 'Lesson 3 — The Major Lines',
    summary: 'Heart, head, life, and fate — quality over superstition.',
    body: [
      'Lines are read through depth, clarity, repair, and relationship to neighboring lines. A change in depth may suggest a chapter shift; a fork may suggest a choice or branching path.',
      'Cross-check emotional themes (heart), cognitive themes (head), vitality and adaptation (life), and vocation or structure (fate, if present).'
    ],
    prompts: [
      'Pick one line and describe three observable qualities (depth, curvature, interruptions) before you say what they might mean.'
    ],
    more: [
      '“Broken” lines are not omens. They may correlate with change — relocation, role shift, learning curves — explored gently in dialogue.'
    ]
  },
  {
    id: 'l4',
    title: 'Lesson 4 — The Mounts',
    summary: 'Where desire and instinct gather under each finger.',
    body: [
      'Mounts add emphasis: they show where energy pools — leadership, depth, creative warmth, eloquence, sensuality, or imagination.',
      'Prominence is relative. Ask which mount leads the hand, rather than judging any mount as “too much.”'
    ],
    prompts: [
      'Name the two most prominent mounts. How would those qualities work together in a single person?'
    ],
    more: [
      'If a mount is quiet, it is not “missing a gift.” It may simply be expressed elsewhere — through lines, finger shape, or lived choice.'
    ]
  },
  {
    id: 'l5',
    title: 'Lesson 5 — Timing, Chapters, and Turning Points',
    summary: 'Symbolic timing — responsible, non-mechanical.',
    body: [
      'Readers sometimes estimate timing along lines; approaches vary widely. Here, timing is treated as interpretive and symbolic — a way to speak about phases, not to claim precision.',
      'Turning points are story moments: recovery, initiation, partnership shifts, creative launches. They deserve care in language.'
    ],
    prompts: [
      'Where on the line does the texture change? What life chapter might that echo — if you asked the person, not the palm alone?'
    ],
    more: [
      'Avoid mechanical year-counting unless you have a method you trust and the querent welcomes it. Uncertainty-friendly language builds trust.'
    ]
  },
  {
    id: 'l6',
    title: 'Lesson 6 — Building a Reading',
    summary: 'Synthesis, balance, and empowering closure.',
    body: [
      'A grounded reading names two or three strong themes, shows how they interact, and leaves room for agency. You are describing weather patterns, not issuing sentences.',
      'Close with strengths, supportive framing, and one reflective question the person can sit with.'
    ],
    prompts: [
      'Draft a three-sentence reading: one theme from lines, one from mounts, one integrating question.'
    ],
    more: [
      'If you are unsure, say so. Honest uncertainty is more respectful than false precision.'
    ]
  }
];

/** Short bullets for marketing / locked preview */
export const pdfBulletPoints = [
  'Reference summaries for quick review',
  'Key palmistry meanings in compact form',
  'Note-taking support for practice sessions',
  'Visual companion to the on-page study module'
];

/** “Inside the PDF” — companion guide contents (locked + unlocked PDF block). */
export const pdfInsideThePdf = [
  'Key reference summaries you can skim before a reading',
  'Line and mount notes in compact form',
  'Printable companion layout for your practice space',
  'Space for observation and short notes alongside the charts'
];
